import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import { collection, onSnapshot, doc, deleteDoc } from "firebase/firestore";
import { db } from "../../../../firebase";
import CustomButton from "../atoms/CustomButton";

const ContentList = ({ theme, event }) => {
  const [contentItems, setContentItems] = useState([]);
  const [selectedContent, setSelectedContent] = useState([]);
  const [selectButtonText, setSelectButtonText] = useState("Select All");
  const navigate = useNavigate();

  useEffect(() => {
    if (!event?.id) return;

    const contentCollectionRef = collection(db, "events", event.id, "content");

    const unsubscribe = onSnapshot(contentCollectionRef, (snapshot) => {
      const items = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setContentItems(items);
    });

    return () => unsubscribe();
  }, [event]);

  const toggleSelectContent = (id) => {
    setSelectedContent((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllContent = () => {
    if (contentItems.every((item) => selectedContent.includes(item.id))) {
      setSelectedContent([]);
      setSelectButtonText("Select All");
    } else {
      setSelectedContent(contentItems.map((item) => item.id));
      setSelectButtonText("Select All");
    }
  };

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(
        selectedContent.map(async (id) => {
          const docRef = doc(db, "events", event.id, "content", id);
          await deleteDoc(docRef);
        })
      );
      setSelectedContent([]);
    } catch (error) {
      console.error("Error deleting content:", error);
    }
  };

  return (
    <>
      {/* Header and action buttons, shown only if there is content */}
      {contentItems.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
          }}
        >
          {/* Header */}
          <Typography
            variant="h5"
            sx={{
              fontFamily: "basic-sans",
              fontWeight: 700,
              color: "white",
            }}
          >
            Current Content Selection
          </Typography>

          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <CustomButton
              theme={theme}
              onClick={handleSelectAllContent}
              sx={{
                backgroundColor: theme.colors.mediumPurple,
                "&:hover": {
                  backgroundColor: theme.colors.blue,
                },
              }}
            >
              {selectButtonText}
            </CustomButton>

            <CustomButton
              theme={theme}
              onClick={handleDeleteSelected}
              disabled={selectedContent.length === 0} // Disable if no content selected
              sx={{
                backgroundColor:
                  selectedContent.length === 0
                    ? `${theme.colors.palePurple} !important` // Custom color for disabled state
                    : theme.colors.mediumPurple, // Default background color

                color:
                  selectedContent.length === 0
                    ? `${theme.colors.darkPurple} !important` // Custom color for disabled state
                    : "white", // Default background color
                "&:hover": {
                  backgroundColor:
                    selectedContent.length === 0
                      ? theme.colors.gray // Keep the same color when disabled
                      : theme.colors.red, // Hover color when enabled
                },
                opacity: selectedContent.length === 0 ? 1 : 1, // Keep opacity consistent with color
                cursor:
                  selectedContent.length === 0 ? "not-allowed" : "pointer", // Proper cursor
              }}
            >
              Delete
            </CustomButton>

            <CustomButton
              onClick={() => navigate("/challenge/admin/events")}
              theme={theme}
              sx={{
                backgroundColor: theme.colors.mediumPurple,
                "&:hover": {
                  backgroundColor: theme.colors.red,
                },
              }}
            >
              Cancel
            </CustomButton>
          </Box>
        </Box>
      )}

      <Grid container spacing={2} sx={{ marginTop: 2 }}>
        {contentItems.map((item) => {
          const thumbnailUrl = item.submissionSaleId
            ? `https://swayer.blob.core.windows.net/${item.submissionSaleId}-pub/_thumb_large.jpg`
            : null;

          return (
            <Grid
              item
              key={item.id}
              xs={6}
              sm={3}
              md={4}
              lg={2}
              sx={{ display: "flex" }}
            >
              <Card
                onClick={() => toggleSelectContent(item.id)}
                sx={{
                  backgroundColor: theme.colors.mediumPurple,
                  color: "white",
                  fontFamily: "basic-sans",
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "20px",
                  border: selectedContent.includes(item.id)
                    ? `2px solid ${theme.colors.red}` // Highlight selected
                    : `2px solid ${theme.colors.darkBlue}`, // Default border
                }}
              >
                {thumbnailUrl && (
                  <CardMedia
                    component="div"
                    sx={{
                      paddingTop: "56.25%", // 16:9 aspect ratio
                      backgroundImage: `url(${thumbnailUrl})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                    alt={item.title || "Content Thumbnail"}
                  />
                )}
                <CardContent>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "basic-sans",
                      color: "white",
                      fontWeight: 700,
                    }}
                  >
                    {item.title || item.id}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default ContentList;
